import React, {
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useEffect,
} from "react"

import authReducer, { initialState, State, Actions } from "./authReducer"
import { app } from "../../utils/firebase"
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as signOutFirebaseUser,
  sendPasswordResetEmail as sendFirebasePasswordResetEmail,
} from "firebase/auth"
import { ContextStatus, initialContextStatus } from "../../types/statusTypes"
import { CurrentUser } from "../../types/authTypes"

type ContextProps = {
  children: ReactNode
}

type ContextValue = State & {
  signInWithPassword: (email: string, password: string) => void
  signOut: () => void
  sendPasswordResetEmail: (email: string) => void
}

const AuthContext = createContext<ContextValue | undefined>(undefined)

export function AuthProvider({ children }: ContextProps) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  if (!app) return null
  const auth = getAuth(app)

  useEffect(() => {
    onAuthStateChanged(auth, authUser => {
      if (authUser) {
        const currentUser: CurrentUser = {
          uid: authUser.uid,
          email: authUser.email ?? "",
        }

        dispatch({
          type: Actions.setUser,
          payload: {
            authUser,
            currentUser,
            isUserLoggedIn: true,
          },
        })
      } else {
        dispatch({
          type: Actions.setUser,
          payload: {
            authUser: null,
            currentUser: null,
            isUserLoggedIn: false,
          },
        })
      }
    })
  }, [])

  const signInWithPassword = async (email: string, password: string) => {
    setStatus({
      state: "pending",
    })

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password)

      setStatus({
        state: "resolved",
        message: "You're in!",
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
      alert((error as Error).message)
    } finally {
      resetStatus()
    }
  }

  const signOut = async () => {
    setStatus({
      state: "pending",
    })

    try {
      await signOutFirebaseUser(auth)

      setStatus({
        state: "resolved",
        message: "You've successfully logged out",
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
      alert((error as Error).message)
    } finally {
      resetStatus()
    }
  }

  const sendPasswordResetEmail = async (email: string) => {
    setStatus({
      state: "pending",
    })

    try {
      await sendFirebasePasswordResetEmail(auth, email)

      setStatus({
        state: "resolved",
        message: "Password reset email sent! Check your inbox.",
      })
      alert("Password reset email sent! Check your inbox.")
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
      alert((error as Error).message)
    } finally {
      resetStatus()
    }
  }

  const resetStatus = () => {
    setStatus(initialContextStatus)
  }

  const setStatus = (status: ContextStatus) => {
    const { state, message, error, showModal } = status

    // if (state === "resolved") {
    //   console.log("message", message)
    // }

    if (state === "rejected") {
      console.log("error", error)
    }

    dispatch({
      type: Actions.setStatus,
      payload: {
        status: {
          state,
          message: message || null,
          error: error || null,
          isPending: state === "pending",
          isResolved: state === "resolved",
          isRejected: state === "rejected",
          showModal: showModal || false,
        },
      },
    })
  }

  const value = {
    status: state.status,
    authUser: state.authUser,
    currentUser: state.currentUser,
    isUserLoggedIn: state.isUserLoggedIn,
    signInWithPassword,
    signOut,
    sendPasswordResetEmail,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useAuth = () => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }

  return context
}

export default useAuth
